import React, { memo, useState } from "react";
import { Button, Icon } from "uikit";
import { useDebouncedCallback } from "use-debounce";

import { positionBasedSortHelper } from "../../../utils";
import { ModelId } from "../../../types/ModelId";
import { StyledRow } from "..";

/**
 * A reusable UI component for reordering a list of models based on their `position` field.
 *
 * Provides buttons for moving the selected item up, down, to the top, or to the bottom of the list.
 * Uses debounced callbacks to prevent excessive updates.
 *
 * @template T - A model type extending `PositionSorter.UpdatePositionPayload`.
 *
 * @param props.models - An array of items with `position` and `id` fields, used for sorting.
 * @param props.selectedId - The currently selected model ID; `false` if none selected.
 * @param props.selectedIndex - The index of the selected model in the `models` array.
 * @param props.updatePosition - A function to update the position of a given model.
 * @param props.includeName - Whether to include the `name` field in the update payload. Defaults to `false`.
 *
 * @example
 * <PositionSorter
 *   models={carClasses}
 *   selectedId="123"
 *   selectedIndex={2}
 *   updatePosition={(payload) => CarClass.updatePosition(payload)}
 *   includeName
 * />
 */
const PositionSorter: React.FC<PositionSorter.Props<any>> = memo(
	({
		models,
		selectedId,
		selectedIndex,
		updatePosition,
		includeName = false,
	}) => {
		const [counter, setCounter] = useState(0);

		const moveUp = useDebouncedCallback(async () => {
			if (!selectedId || selectedIndex === null) return;
			const dowItem = models[selectedIndex - counter];
			const upItem = models[selectedIndex];

			// up
			await updatePosition({
				id: upItem.id,
				position: dowItem.position,
				...(includeName ? { name: upItem.name } : {}),
			});
			// dow
			await updatePosition({
				id: dowItem.id,
				position: positionBasedSortHelper.makePositionUnique(
					upItem.position,
					dowItem.position,
				),
				...(includeName ? { name: dowItem.name } : {}),
			});
			setCounter(0);
		}, 100);

		const moveDown = useDebouncedCallback(async () => {
			if (!selectedId || selectedIndex === null) return;

			const dowItem = models[selectedIndex];
			const upItem = models[selectedIndex + counter];

			// dow
			await updatePosition({
				id: dowItem.id,
				position: upItem.position,
				...(includeName ? { name: dowItem.name } : {}),
			});
			// up
			await updatePosition({
				id: upItem.id,
				position: positionBasedSortHelper.makePositionUnique(
					dowItem.position,
					upItem.position,
				),
				...(includeName ? { name: upItem.name } : {}),
			});
			setCounter(0);
		}, 100);

		const moveToTop = useDebouncedCallback(async () => {
			if (!selectedId || selectedIndex === null) return;

			const firstItem = models[0];
			const upItem = models[selectedIndex];

			// first
			await updatePosition({
				id: upItem.id,
				position: firstItem.position,
				...(includeName ? { name: upItem.name } : {}),
			});
			// last
			await updatePosition({
				id: firstItem.id,
				position: positionBasedSortHelper.makePositionUnique(
					upItem.position,
					firstItem.position,
				),
				...(includeName ? { name: firstItem.name } : {}),
			});
		}, 100);

		const moveToBottom = useDebouncedCallback(async () => {
			if (!selectedId || selectedIndex === null) return;
			const dowItem = models[selectedIndex];
			const lastItem = models.at(-1);
			if (!lastItem) return;
			// last
			await updatePosition({
				id: dowItem.id,
				position: lastItem.position,
				...(includeName ? { name: dowItem.name } : {}),
			});
			// first
			await updatePosition({
				id: lastItem.id,
				position: positionBasedSortHelper.makePositionUnique(
					dowItem.position,
					lastItem.position,
				),
				...(includeName ? { name: lastItem.name } : {}),
			});
		}, 100);

		return (
			<StyledRow gap="5px">
				<Button.Button
					variant="secondary"
					icon={<Icon id="arrow-up" size={23} />}
					onClick={() => {
						if (selectedIndex && counter < selectedIndex) {
							setCounter((prev) => prev + 1);
						}
						moveUp();
					}}
					disabled={!selectedId || selectedIndex === 0}
				/>
				<Button.Button
					variant="secondary"
					icon={<Icon id="arrow-down" size={23} />}
					onClick={() => {
						if (
							counter <
							(selectedIndex != null
								? models.length - 1 - selectedIndex
								: NaN)
						) {
							setCounter((prev) => prev + 1);
						}
						moveDown();
					}}
					disabled={
						!selectedId || selectedIndex === models.length - 1
					}
				/>
				<Button.Button
					variant="secondary"
					icon={<Icon id="arrow-top" size={23} />}
					onClick={moveToTop}
					disabled={!selectedId || selectedIndex === 0}
				/>
				<Button.Button
					variant="secondary"
					icon={<Icon id="arrow-bottom" size={23} />}
					onClick={moveToBottom}
					disabled={
						!selectedId || selectedIndex === models.length - 1
					}
				/>
			</StyledRow>
		);
	},
);

declare namespace PositionSorter {
	/**
	 * Payload shape passed to `updatePosition`.
	 */
	export interface UpdatePositionPayload {
		id: ModelId;
		position: number;
		[key: string]: any;
	}

	/**
	 * Component props.
	 *
	 * @template T - A model extending the required base shape.
	 */
	export interface Props<T extends UpdatePositionPayload> {
		/** List of sortable items. */
		models: T[];

		/** Currently selected item ID. */
		selectedId: ModelId | false;

		/** Index of the selected item in the list. */
		selectedIndex: number | null;

		/** Function to send updates to the backend or service. */
		updatePosition: (data: UpdatePositionPayload) => Promise<void>;

		/** Whether to include `name` field in the update payload. */
		includeName?: boolean;
	}
}

export default PositionSorter;
